import React from 'react';

import {
  BaseItemStatus,
  ItemRoot,
  StatusWrap,
  Infos,
  BaseName,
  Address,
} from './styles';
import { colors } from '../tokens';

export type { BaseItemStatus } from './styles';

export interface BaseSelectedItemProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  /** Nome/apelido da base (ex.: "Refeitório fundos"). */
  nickname: string;
  /** Endereço da base (ex.: "Rua Pasteur, 89 - S C"). */
  address?: string;
  /** Estado de preenchimento da base. @default 'pending' */
  status?: BaseItemStatus;
  /** Marca o item como em foco/selecionado. @default false */
  selected?: boolean;
  /** Clique no item (selecionar a base). */
  onClick?: () => void;
}

const ICON_SIZE = 24;

const CheckIcon = ({ color }: { color: string }): React.ReactElement => (
  <svg
    width={ICON_SIZE}
    height={ICON_SIZE}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 12.5l5 5L20 6.5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClockIcon = ({ color }: { color: string }): React.ReactElement => (
  <svg
    width={ICON_SIZE}
    height={ICON_SIZE}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.8" />
    <path
      d="M12 7.5V12l3 2"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WarningIcon = ({ color }: { color: string }): React.ReactElement => (
  <svg
    width={ICON_SIZE}
    height={ICON_SIZE}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 3.5l9 16H3l9-16z"
      stroke={color}
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M12 10v4" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="16.5" r="1" fill={color} />
  </svg>
);

const STATUS_LABEL: Record<BaseItemStatus, string> = {
  pending: 'pendente',
  complete: 'preenchido',
  error: 'com erro',
};

/**
 * BaseSelectedItem — item compacto de base para a coluna lateral do fluxo de
 * abastecimento. Apresentacional: NÃO acessa o estado/hook; quem calcula
 * `status`/`selected` é o container.
 *
 * @example
 * <BaseSelectedItem
 *   nickname="Refeitório fundos"
 *   address="Rua Pasteur, 89 - S C"
 *   status="complete"
 *   selected
 *   onClick={() => selectBase('BIC1')}
 * />
 */
const BaseSelectedItem = ({
  nickname,
  address,
  status = 'pending',
  selected = false,
  onClick,
  ...rest
}: BaseSelectedItemProps): React.ReactElement => {
  const iconColor = selected ? colors.primary : colors.textContrast;

  const renderIcon = (): React.ReactElement => {
    if (status === 'error') return <WarningIcon color={colors.error} />;
    if (status === 'complete') return <CheckIcon color={iconColor} />;
    return <ClockIcon color={iconColor} />;
  };

  return (
    <ItemRoot
      type="button"
      $selected={selected}
      aria-pressed={selected}
      onClick={onClick}
      {...rest}
    >
      <StatusWrap aria-label={`Base ${STATUS_LABEL[status]}`}>
        {renderIcon()}
      </StatusWrap>
      <Infos>
        <BaseName $selected={selected}>{nickname}</BaseName>
        {address && <Address>{address}</Address>}
      </Infos>
    </ItemRoot>
  );
};

export default BaseSelectedItem;
